import React, { useState, useEffect } from 'react';
import { getCropTypes, getVarietiesByCropType } from '../../services/v2/adminService';
import { createPlantingCycle } from '../../services/v2/plantingCycleService';

const NewPlantingCycleModal = ({ isOpen, onClose, onCycleCreated, cropId }) => {
    const [cropTypes, setCropTypes] = useState([]);
    const [varieties, setVarieties] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        cropTypeId: '',
        varietyId: '',
        startDate: '',
        plannedHarvestDate: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchCropTypes();
            // Default startDate to today
            const today = new Date().toISOString().split('T')[0];
            setFormData(prev => ({
                ...prev,
                name: '',
                startDate: today,
                plannedHarvestDate: ''
            }));
            setIsSubmitting(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (formData.cropTypeId) {
            fetchVarieties(formData.cropTypeId);
        } else {
            setVarieties([]);
            setFormData(prev => ({ ...prev, varietyId: '' }));
        }
    }, [formData.cropTypeId]);

    const fetchCropTypes = async () => {
        try {
            const data = await getCropTypes();
            setCropTypes(data || []);
        } catch (error) {
            console.error('Error fetching crop types', error);
        }
    };

    const fetchVarieties = async (cropTypeId) => {
        try {
            const data = await getVarietiesByCropType(cropTypeId);
            setVarieties(data || []);
        } catch (error) {
            console.error('Error fetching varieties', error);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            const payload = {
                name: formData.name || null,
                cropId: cropId,
                varietyId: formData.varietyId,
                startDate: formData.startDate,
                plannedHarvestDate: formData.plannedHarvestDate || null
            };
            await createPlantingCycle(payload);
            onCycleCreated();
            onClose();
        } catch (error) {
            console.error('Error creating planting cycle', error);
            alert('Error al crear la siembra');
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900 bg-opacity-80">
            <div className="w-full max-w-md bg-white shadow-lg rounded">
                <div className="relative flex items-center p-4 text-black border-b">
                    <h2 className="text-xl font-semibold">Nueva Siembra</h2>
                    <button type="button" className="absolute top-0 right-0 mt-4 mr-4 hover:text-red-500 w-6 h-6 disabled:opacity-50 disabled:cursor-not-allowed" onClick={onClose} disabled={isSubmitting}>
                        <span className="material-icons text-2xl font-bold">close</span>
                    </button>
                </div>

                <div className="p-4 text-black">
                    <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
                        <div>
                            <label className="block font-semibold mb-1">Nombre (Opc.)</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                maxLength={40}
                                placeholder="Ej: Lote Norte"
                                className="block w-full p-2 border border-gray-300 rounded"
                            />
                            <div className="text-right text-[10px] text-gray-400 mt-0.5">{formData.name.length}/40</div>
                        </div>

                        <div>
                            <label className="block font-semibold mb-1">Tipo de Cultivo <span className="text-red-500">*</span></label>
                            <select
                                name="cropTypeId"
                                value={formData.cropTypeId}
                                onChange={handleChange}
                                required
                                className="block w-full p-2 border border-gray-300 rounded"
                            >
                                <option value="">Seleccione Tipo de Cultivo</option>
                                {cropTypes.map(ct => (
                                    <option key={ct.id} value={ct.id}>{ct.name}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block font-semibold mb-1">Variedad <span className="text-red-500">*</span></label>
                            <select
                                name="varietyId"
                                value={formData.varietyId}
                                onChange={handleChange}
                                required
                                disabled={!formData.cropTypeId}
                                className="block w-full p-2 border border-gray-300 rounded disabled:bg-gray-100"
                            >
                                <option value="">Seleccione Variedad</option>
                                {varieties.map(v => (
                                    <option key={v.id} value={v.id}>{v.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block font-semibold mb-1 text-sm">Fecha de Siembra <span className="text-red-500">*</span></label>
                                <input
                                    type="date"
                                    name="startDate"
                                    value={formData.startDate}
                                    onChange={handleChange}
                                    required
                                    className="block w-full p-2 border border-gray-300 rounded text-sm"
                                />
                            </div>
                            <div>
                                <label className="block font-semibold mb-1 text-sm">Fecha Esp. Cosecha (Opc.)</label>
                                <input
                                    type="date"
                                    name="plannedHarvestDate"
                                    value={formData.plannedHarvestDate}
                                    onChange={handleChange}
                                    className="block w-full p-2 border border-gray-300 rounded text-sm"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full mt-4 bg-blue-600 text-white font-medium py-2 rounded hover:bg-blue-700 transition flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {isSubmitting
                                ? <>
                                    <span className="material-icons text-sm animate-spin">refresh</span>
                                    Creando...
                                  </>
                                : 'Crear Siembra'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default NewPlantingCycleModal;
